"use client";

"use client";

import { useState, useRef, useCallback, useEffect } from 'react';
import ChatBoxView from "../components/chat/chatview";
import { IChatMessage } from "@/types/chat-types";
import { chatService } from '../services/chatService';
import { useLocation } from 'react-router-dom';
import ModernChat from '../components/chat/modern/ModernChat';

const ChatWindow = () => {
    const location = useLocation();
    const playbookId = location.state?.playbookId;
    const playbookName = location.state?.playbookName;
    const sessionId = location.state?.sessionId;
    console.log("playbookId", playbookId);
    
    // Always use modern chat interface for all playbooks (including interactive ones)
    const useModernInterface = true;
    const [messages, setMessages] = useState<IChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const cleanupRef = useRef<(() => void) | null>(null);
    const [userInput, setUserInput] = useState(null);

    // Reset local state when playbook changes
    useEffect(() => {
        // Reset local component state
        setMessages([]);
        setIsLoading(false);
        setUserInput(null);
        
        // Clear any localStorage for legacy chat (to prevent interference)
        try {
            localStorage.removeItem('messages');
        } catch (e) {
            // Silently ignore localStorage errors
        }
        
        // Cleanup any ongoing requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        
    }, [playbookId, playbookName]); // Reset when playbook changes

    // Helper functions to reduce complexity
    const createMessageId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const saveMessageToLocalStorage = (message: IChatMessage) => {
        try {
            const messages = JSON.parse(localStorage.getItem('messages') || '[]');
            messages.push(message);
            localStorage.setItem('messages', JSON.stringify(messages));
        } catch (error) {
            console.error('Error saving message to localStorage:', error);
        }
    };

    const addUserMessage = (message: string) => {
        const userMessage: IChatMessage = {
            id: createMessageId('user'),
            text: message,
            sender: 'user',
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMessage]);
        saveMessageToLocalStorage(userMessage);
    };

    const addErrorMessage = (error: unknown, context: string) => {
        const errorMessage: IChatMessage = {
            id: createMessageId('error'),
            text: `Error: ${error instanceof Error ? error.message : context}`,
            sender: 'assistant',
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMessage]);
        saveMessageToLocalStorage(errorMessage);
        setIsLoading(false);
        cleanupRef.current = null;
    };

    const handleStreamingChunk = (chunk: any, isEvent: boolean, eventType?: string) => {
        console.log('Received chunk:', chunk, 'isEvent:', isEvent, 'eventType:', eventType);
        
        if (eventType === 'tool' && chunk?.["type"] === "USER_INPUT") {
            setUserInput(chunk);
            return;
        }

        setMessages(prev => {
            const newMessages = [...prev];
            
            if (isEvent) {
                const eventMessage = createEventMessage(chunk, eventType);
                saveMessageToLocalStorage(eventMessage);
                return [...newMessages, eventMessage];
            }
            
            return updateStreamingMessage(newMessages, chunk);
        });
    };

    const createEventMessage = (chunk: any, eventType?: string): IChatMessage => {
        if (eventType === 'tool' && chunk?.["validation"] === "SUCCESS") {
            return {
                id: createMessageId('sys'),
                text: "Validation Successfull for " + chunk?.["slot_id"],
                sender: 'assistant',
                timestamp: new Date().toISOString(),
            };
        }
        
        return {
            id: createMessageId('sys'),
            text: chunk,
            sender: 'assistant',
            timestamp: new Date().toISOString(),
        };
    };

    const updateStreamingMessage = (messages: IChatMessage[], chunk: any): IChatMessage[] => {
        const lastMessage = messages[messages.length - 1];
        
        if (lastMessage && lastMessage.sender === 'assistant') {
            lastMessage.text = chunk;
            lastMessage.timestamp = new Date().toISOString();
            return [...messages];
        }
        
        const newMessage: IChatMessage = {
            id: createMessageId('assistant'),
            text: chunk,
            sender: 'assistant',
            timestamp: new Date().toISOString()
        };
        return [...messages, newMessage];
    };

    const setupAbortController = () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        
        return controller;
    };

    const handleSendMessage = useCallback(async (message?: string) => {
        if (!message?.trim()) return;

        try {
            if (message !== "Start the execution") {
                addUserMessage(message);
            }
            
            setIsLoading(true);
            const controller = setupAbortController();

            if (!playbookId) {
                throw new Error('Playbook ID is required');
            }

            const cleanup = await chatService.sendMessage(
                message,
                userInput,
                playbookId.toString(),
                sessionId,
                messages,
                handleStreamingChunk,
                () => {
                    setIsLoading(false);
                    cleanupRef.current = null;
                },
                (error: Error) => {
                    console.error('Error in chat service:', error);
                    addErrorMessage(error, error.message);
                },
                setUserInput,
                controller.signal
            );

            cleanupRef.current = cleanup;
        } catch (error: unknown) {
            console.error('Error sending message:', error);
            addErrorMessage(error, 'Unknown error occurred');
        }
    }, [userInput]);

    const handleStopGeneration = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    }, []);

    // Render modern chat interface with dynamic playbook support
    if (useModernInterface && playbookId) {
        return (
            <ModernChat 
                key={playbookId} // Force component remount when playbook changes
                playbookId={playbookId} 
                playbookName={playbookName || 'Interactive Dialogue'}
            />
        );
    }
    
    // Fallback for legacy support if no playbook ID
    if (useModernInterface) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50">
                <div className="text-center p-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">No Playbook Selected</h2>
                    <p className="text-gray-600">Please select a playbook to start chatting.</p>
                </div>
            </div>
        );
    }

    // Render original chat interface for other playbooks
    return (
        <main style={{ height: "94vh", overflow: "hidden" }} className="container mx-auto p-4">
            <div className="mb-1 ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Chat with Agent
                </h1>
                <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1 mb-6">
                    Chat with your agent about this playbook.
                </p>
            </div>
            <ChatBoxView 
                initMessages={messages} 
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
                onStopGeneration={handleStopGeneration}
                userInput={userInput}
                setUserInput={setUserInput}
            />
        </main>
    );
};

export default ChatWindow;